import { Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect, lazy, Suspense } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { useAuth } from '@/lib/useAuth';
import { Layout } from '@/components/Layout';
import { LoginPage } from '@/pages/LoginPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { CampaignsPage } from '@/pages/campaigns/CampaignsPage';
import { CampaignCreatePage } from '@/pages/campaigns/CampaignCreatePage';
import { CampaignDetailPage } from '@/pages/campaigns/CampaignDetailPage';
import { CallLogPage } from '@/pages/CallLogPage';
import { InsightsPage } from '@/pages/InsightsPage';
import { PhoneNumbersPage } from '@/pages/PhoneNumbersPage';
// Signup is parked — Hercules is not accepting self-service signups.
// To re-enable: restore this import, restore the /signup route below, restore the
// ProtectedRoute redirect, and set HERCULES_SIGNUP_ENABLED=true on the backend.
// import { SignupPage } from '@/pages/SignupPage';
import { MyAgentPage } from '@/pages/MyAgentPage';
const StudioPage = lazy(() =>
  import('@/pages/studio/StudioPage').then((m) => ({ default: m.StudioPage })),
);

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const [checking, setChecking] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const { loading: authLoading } = useAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setAuthenticated(!!firebaseUser);
      setChecking(false);
    });
    return () => unsubscribe();
  }, []);

  if (checking || authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!authenticated) {
    return <Navigate to="/login" replace />;
  }

  // Signup parked — incomplete signups no longer redirected (no signup page).
  // To re-enable when signup is back, restore:
  //   if (user && user.client_type === 'customer' && user.signup_step < 3) {
  //     return <Navigate to="/signup" replace />;
  //   }

  return <>{children}</>;
}

function RequireCap({ cap, children }: { cap: string; children: React.ReactNode }) {
  const { hasCap, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  if (!hasCap(cap)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* Signup parked. Restore: <Route path="/signup" element={<SignupPage />} /> */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="calls" element={<CallLogPage />} />
        <Route path="insights" element={<InsightsPage />} />
        <Route path="my-agent" element={<RequireCap cap="my_agent"><MyAgentPage /></RequireCap>} />
        <Route path="campaigns" element={<RequireCap cap="campaigns"><CampaignsPage /></RequireCap>} />
        <Route path="campaigns/new" element={<RequireCap cap="campaigns"><CampaignCreatePage /></RequireCap>} />
        <Route path="campaigns/:id" element={<RequireCap cap="campaigns"><CampaignDetailPage /></RequireCap>} />
        <Route path="phone-numbers" element={<RequireCap cap="phone_numbers"><PhoneNumbersPage /></RequireCap>} />
        <Route
          path="studio"
          element={
            <RequireCap cap="studio">
              <Suspense
                fallback={
                  <div className="flex items-center justify-center py-12">
                    <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
                  </div>
                }
              >
                <StudioPage />
              </Suspense>
            </RequireCap>
          }
        />
      </Route>
    </Routes>
  );
}

export default App;
